import { isNotDefined, isDefined } from '@togglecorp/fujs';
import { sumSafe, getNumberOfMonths, getNumberOfDays, minSafe, maxSafe } from './common.js';

// TODO: Add test
function getBounds(numList, bounds) {
  if (isNotDefined(numList) || numList.length === 0) {
    const min = bounds?.min ?? 0;
    const max = bounds?.max ?? min + 1;
    return {
      min,
      max
    };
  }
  let newList = [...numList];
  if (isDefined(bounds)) {
    newList = [...numList, bounds.min, bounds.max];
  }
  const min = minSafe(newList) ?? 0;
  const max = maxSafe(newList) ?? min + 5;
  return {
    min,
    max: max === min ? min + 5 : max
  };
}

// TODO: Add test

function getPathData(pointList) {
  if (isNotDefined(pointList) || pointList.length < 2) {
    return undefined;
  }
  return pointList.map((point, i) => {
    if (i === 0) {
      return `M${point.x} ${point.y}`;
    }
    return `L${point.x} ${point.y}`;
  }).join(' ');
}

// TODO: Add test
function scaleNormalizedValue(normalizedValue, type) {
  if (type === 'exponential') {
    return Math.exp(normalizedValue) / Math.exp(1);
  }
  if (type === 'log10') {
    if (normalizedValue === 0) {
      return 0;
    }
    return normalizedValue * Math.log10(normalizedValue * 10);
  }
  if (type === 'sqrt') {
    return Math.sqrt(normalizedValue);
  }
  if (type === 'cbrt') {
    return Math.cbrt(normalizedValue);
  }
  return normalizedValue;
}

// TODO: Update test
function getScaleFunction(domain, range, offset, inverted = false, scale = 'linear') {
  const rangeSize = range.max - range.min - (offset.start + offset.end);
  const domainSize = domain.max - domain.min;
  return value => {
    if (domainSize === 0) {
      return domain.min;
    }
    const normalizedValue = (value - domain.min) / domainSize;
    const scaledValue = scaleNormalizedValue(normalizedValue, scale);
    if (inverted) {
      return rangeSize + offset.start - rangeSize * scaledValue;
    }
    return offset.start + rangeSize * scaledValue;
  };
}
function getIntervals(bounds, numPoints) {
  if (isNotDefined(numPoints) || numPoints === 0) {
    return [];
  }
  if (numPoints === 1) {
    return [bounds.min];
  }
  const diff = (bounds.max - bounds.min) / (numPoints - 1);
  const ticks = bounds.max === 0 ? [] : Array.from(Array(numPoints).keys()).map(key => {
    const tick = bounds.min + diff * key;
    return tick;
  });
  return ticks;
}
function getEvenlyDistributedExcess(excess) {
  return {
    left: Math.floor(excess / 2),
    right: Math.ceil(excess / 2)
  };
}
function getEvenDistribution(min, max, distribution) {
  const diff = max - min;
  if (diff === 0) {
    return {
      left: Math.floor(distribution / 2),
      right: Math.ceil(distribution / 2)
    };
  }
  const remainder = diff % (distribution - 1);
  const additional = remainder === 0 ? 0 : distribution - 1 - remainder;
  return getEvenlyDistributedExcess(additional);
}
function getChartDimensions({
  chartMargin,
  chartPadding,
  xAxisHeight,
  yAxisWidth,
  chartSize,
  numXAxisTicks
}) {
  const initialLeftOffset = chartMargin.left + yAxisWidth + chartPadding.left;
  const initialRightOffset = chartMargin.right + chartPadding.right;
  const topOffset = chartMargin.top + chartPadding.top;
  const bottomOffset = chartMargin.bottom + chartPadding.bottom + xAxisHeight;
  const renderableDataAreaWidth = chartSize.width - initialLeftOffset - initialRightOffset;
  const additionalHorizontalOffset = renderableDataAreaWidth / (numXAxisTicks + 1);
  const dataAreaWidth = Math.max(additionalHorizontalOffset * (numXAxisTicks - 1), 0);
  const leftOffset = initialLeftOffset + additionalHorizontalOffset;
  const rightOffset = initialRightOffset + additionalHorizontalOffset;
  const dataAreaHeight = Math.max(chartSize.height - topOffset - bottomOffset, 0);
  return {
    dataAreaSize: {
      width: dataAreaWidth,
      height: dataAreaHeight
    },
    dataAreaOffset: {
      top: topOffset,
      right: rightOffset,
      bottom: bottomOffset,
      left: leftOffset
    }
  };
}
function getYDomain(yValues, numYAxisTicks, yValueStartsFromZero, chartGroupingMode) {
  let yValuesFlat;
  if (chartGroupingMode === 'sum') {
    yValuesFlat = yValues.map(({
      yValue
    }) => sumSafe(yValue.map(({
      value
    }) => value))).filter(isDefined);
  } else {
    yValuesFlat = yValues.flatMap(({
      yValue
    }) => yValue.map(({
      value
    }) => value)).filter(isDefined);
  }
  const yBounds = getBounds(yValuesFlat);
  if (yValueStartsFromZero) {
    const {
      left,
      right
    } = getEvenDistribution(0, yBounds.max, numYAxisTicks);
    return {
      min: 0,
      max: yBounds.max + left + right
    };
  }
  const {
    left,
    right
  } = getEvenDistribution(yBounds.min, yBounds.max, numYAxisTicks);
  return {
    min: yBounds.min - left,
    max: yBounds.max + right
  };
}
function getTemporalDiff(min, max) {
  const minDate = new Date(min);
  const maxDate = new Date(max);
  const yearsDiff = maxDate.getFullYear() - minDate.getFullYear();
  const monthsDiff = getNumberOfMonths(minDate, maxDate);
  const daysDiff = getNumberOfDays(minDate, maxDate);
  return {
    year: yearsDiff,
    month: monthsDiff,
    day: daysDiff
  };
}
const groupingModeMapping = {
  stacked: 'sum',
  'side-by-side': 'none'
};
function extractBarChartProps(combinedProps) {
  const {
    data,
    className,
    yValueKeys,
    colorSelector,
    chartAxesOptions,
    barListOptions,
    chartOptions,
    barGroupingMode = 'side-by-side',
    ...chartContainerProps
  } = combinedProps;
  return {
    commonProps: {
      data,
      yValueKeys,
      colorSelector
    },
    containerProps: {
      ...chartContainerProps,
      className
    },
    chartAxesProps: {
      ...chartAxesOptions
    },
    barListProps: {
      ...barListOptions,
      yValueKeys,
      barGroupingMode
    },
    chartDataProps: {
      yValueKeys,
      chartGroupingMode: groupingModeMapping[barGroupingMode],
      ...chartOptions
    }
  };
}
function extractLineChartProps(combinedProps) {
  const {
    data,
    className,
    yValueKeys,
    colorSelector,
    chartAxesOptions,
    chartOptions,
    ...chartContainerProps
  } = combinedProps;
  return {
    commonProps: {
      data,
      yValueKeys,
      colorSelector
    },
    containerProps: {
      ...chartContainerProps,
      className
    },
    chartAxesProps: {
      ...chartAxesOptions
    },
    chartDataProps: {
      yValueKeys,
      chartGroupingMode: 'sum',
      ...chartOptions
    }
  };
}

export { extractBarChartProps, extractLineChartProps, getBounds, getChartDimensions, getEvenDistribution, getEvenlyDistributedExcess, getIntervals, getPathData, getScaleFunction, getTemporalDiff, getYDomain };
//# sourceMappingURL=chart.js.map
